// Sawyer 07 quickbook-bed-addons (C1)
/**
 * "Additional services" on the Quick Book card — the client half.
 *
 * GET /api/admin/bookings/service-options → the priced catalog for a DRAFT
 * booking (camelCase, like the visit's own /:id/service-options). The 201 of
 * POST /api/admin/bookings gains `additional_services` when services were
 * sent and the server knows the field.
 *
 * A NEW file (MERGE-NOTES: new endpoints' client code stays out of api.ts).
 */
import {
  parseErrorBody,
  readSession,
  type AdminBookingCreateResponse,
  type AdminBookingCreated,
  type ApiResponse,
} from './api.js';

export type AdminDraftServiceOption = {
  kind: 'addOn' | 'service';
  id: string;
  label: string;
  /** What the first day (or the visit) is charged, for this many dogs. */
  totalCents: number;
  /** One line per calendar day of the stay; one for a single day. */
  dayTotals: Array<{ date: string; totalCents: number }>;
  durationMinutes: number | null;
};

export type AdminDraftServiceOptions = {
  options: AdminDraftServiceOption[];
  dogCount: number;
  date: string;
  stayDates: string[];
};

/** The 201's per-item report (snake_case like the half-day keys). */
export type AdditionalServicesWire = Array<{
  label: string;
  add_on_id: string | null;
  service_type_id: string | null;
  every_day: boolean;
  added: boolean;
  legs: Array<AdminBookingCreated & { parent_booking_id?: string | null; add_on_id?: string | null }>;
  total_cents: number;
  error: { code: string; message: string } | null;
}>;

/**
 * The create's 201 as Quick Book reads it. `additional_services` absent
 * although services were sent = an older server stripped them (the card then
 * says the services were not added).
 */
export type QuickBookCreateResponse = AdminBookingCreateResponse & { additional_services?: AdditionalServicesWire };

/** The quote's answer with its HTTP status, so a 403/404 (older server, floor role) reads as "not offered", not as an error. */
export type AdminDraftServiceOptionsResult = ApiResponse<AdminDraftServiceOptions> & {
  status: number;
  /** The server answered 2xx with something that is not this quote (it does not know the route). */
  unrecognised?: boolean;
};

/**
 * The draft quote. A plain fetch (not `request`) because the caller must tell
 * 403/404 — the route is not on this server yet, or this role may not quote —
 * from a real failure, and `ApiResponse` carries no status. Errors still go
 * through `parseErrorBody` (401 → session expiry, machine codes → copy).
 */
export async function fetchQuickBookServiceOptions(
  apiUrl: string,
  query: { customerId: string; date: string; endDate?: string | null; dogCount: number; serviceTypeId?: string | null },
): Promise<AdminDraftServiceOptionsResult> {
  const params = new URLSearchParams({ customerId: query.customerId, date: query.date, dogCount: String(query.dogCount) });
  if (query.endDate) params.set('endDate', query.endDate);
  if (query.serviceTypeId) params.set('serviceTypeId', query.serviceTypeId);
  const { token } = readSession();
  try {
    const res = await fetch(`${apiUrl}/api/admin/bookings/service-options?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    if (res.ok) {
      const q = body as Partial<AdminDraftServiceOptions> | undefined;
      if (q && Array.isArray(q.options) && Array.isArray(q.stayDates) && typeof q.dogCount === 'number') {
        return { status: res.status, data: q as AdminDraftServiceOptions };
      }
      return { status: res.status, error: 'Unexpected answer from the server.', unrecognised: true };
    }
    const parsed = parseErrorBody(res.status, body, { fallback: 'Could not load the service list.' });
    return { status: res.status, error: parsed.message, ...(parsed.code ? { code: parsed.code } : {}) };
  } catch {
    return { status: 0, error: 'Unable to reach the server.', isRetryable: true };
  }
}
